import { useState, useEffect } from 'react';
import { storedFileSrc } from '../lib/api';
import { publicSiteApi, type PublicSiteAdmin, type SiteStat } from '../lib/publicSiteApi';

/**
 * The school's editor for its PUBLIC page. Everything here is what a prospective parent sees at the
 * school's web address. The achievements & gallery on the public page come from news posts marked
 * "show publicly" (on the News screen) — this editor controls the surrounding site.
 */
export function PublicSiteAdminPage() {
  const [s, setS] = useState<PublicSiteAdmin | null>(null);
  const [msg, setMsg] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => { publicSiteApi.admin().then(setS).catch(() => setS(null)); }, []);

  const flash = (m: string) => { setMsg(m); setTimeout(() => setMsg(null), 2500); };
  const wrap = async (fn: () => Promise<PublicSiteAdmin>, ok: string) => {
    setBusy(true);
    try { setS(await fn()); flash(ok); } catch { flash('Could not save.'); } finally { setBusy(false); }
  };

  if (!s) return <p style={{ color: 'var(--ink-3)' }}>Loading…</p>;

  const heroSrc = storedFileSrc(s.heroImageUrl);   // ⚠ not apiUrl — see storedFileSrc

  return (
    <div>
      <div style={{ marginBottom: 20 }}>
        <div className="eyebrow" style={{ marginBottom: 6 }}>Settings</div>
        <h1 className="h-display" style={{ fontSize: 28 }}>Public page</h1>
        <p style={{ color: 'var(--ink-2)', fontSize: 13, marginTop: 4, maxWidth: 680 }}>
          Your school's public showcase at your web address — what prospective parents see before
          logging in. Achievements shown here are the news posts you've marked "show publicly."
        </p>
      </div>

      {msg && <div className="pill" style={{ padding: '8px 12px', borderRadius: 'var(--r-md)', justifyContent: 'flex-start', marginBottom: 14, fontSize: 13 }}>{msg}</div>}

      {/* master switch */}
      <div className="card" style={{ padding: 18, marginBottom: 16, display: 'flex', alignItems: 'center', gap: 14, maxWidth: 820 }}>
        <div style={{ flex: 1 }}>
          <div style={{ fontWeight: 600 }}>{s.isEnabled ? 'Public page is LIVE' : 'Public page is off'}</div>
          <div style={{ fontSize: 12, color: 'var(--ink-3)' }}>
            {s.isEnabled ? 'Visitors to your address see the showcase.' : 'Your address goes straight to login until you turn this on.'}
          </div>
        </div>
        <button className={`btn ${s.isEnabled ? '' : 'btn-accent'}`} disabled={busy}
          onClick={() => wrap(() => publicSiteApi.setEnabled(!s.isEnabled), s.isEnabled ? 'Public page turned off.' : 'Public page is live!')}>
          {s.isEnabled ? 'Turn off' : 'Turn on'}
        </button>
      </div>

      <div style={{ display: 'grid', gap: 16, gridTemplateColumns: 'minmax(0,1fr) minmax(0,1fr)', maxWidth: 820 }}>
        {/* hero */}
        <Card title="Hero">
          <Field label="Headline (over the school name)">
            <input className="input" defaultValue={s.heroHeadline ?? ''} placeholder="Shaping tomorrow's leaders"
              onBlur={(e) => wrap(() => publicSiteApi.updateHero(e.target.value || null), 'Saved.')} />
          </Field>
          <Field label="Hero background image">
            <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
              {heroSrc
                ? <img src={heroSrc} alt="" style={{ width: 96, height: 54, objectFit: 'cover', borderRadius: 8, border: '1px solid var(--line)' }} />
                : <div style={{ width: 96, height: 54, borderRadius: 8, background: 'var(--surface-2,#f2f2f0)', display: 'grid', placeItems: 'center', fontSize: 11, color: 'var(--ink-3)' }}>none</div>}
              <input type="file" accept="image/*" disabled={busy}
                onChange={(e) => { const f = e.target.files?.[0]; if (f) wrap(() => publicSiteApi.uploadHero(f), 'Image updated.'); }} />
            </div>
          </Field>
        </Card>

        {/* admissions */}
        <Card title="Admissions">
          <label style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 14, cursor: 'pointer', marginBottom: 10 }}>
            <input type="checkbox" checked={s.admissionsOpen}
              onChange={(e) => wrap(() => publicSiteApi.updateAdmissions(e.target.checked, s.admissionsNote), 'Saved.')} />
            Admissions open (shows a call-to-action button)
          </label>
          <Field label="Admissions note">
            <input className="input" defaultValue={s.admissionsNote ?? ''} placeholder="Admissions open for 2026–27"
              onBlur={(e) => wrap(() => publicSiteApi.updateAdmissions(s.admissionsOpen, e.target.value || null), 'Saved.')} />
          </Field>
          <div style={{ fontSize: 11, color: 'var(--ink-3)', marginTop: 4 }}>
            The enquiry button also needs the online form switched on under <strong>Admissions → Settings</strong>.
            Until then, the button shows a "contact the school" message.
          </div>
        </Card>

        {/* about */}
        <Card title="About" wide>
          <Field label="Title">
            <input className="input" defaultValue={s.aboutTitle ?? ''} placeholder="About our school"
              onBlur={(e) => saveAbout(s, { title: e.target.value }, wrap)} />
          </Field>
          <Field label="Description">
            <textarea className="input" rows={4} defaultValue={s.aboutBody ?? ''} style={{ resize: 'vertical' }}
              placeholder="A few paragraphs about your institution…"
              onBlur={(e) => saveAbout(s, { body: e.target.value }, wrap)} />
          </Field>
          <div style={{ display: 'flex', gap: 12 }}>
            <Field label="Established year">
              <input className="input" type="number" defaultValue={s.establishedYear ?? ''} placeholder="1982"
                onBlur={(e) => saveAbout(s, { year: e.target.value ? Number(e.target.value) : null }, wrap)} />
            </Field>
            <Field label="Boards offered">
              <input className="input" defaultValue={s.boardsOffered ?? ''} placeholder="CBSE · ICSE · State · PU"
                onBlur={(e) => saveAbout(s, { boards: e.target.value }, wrap)} />
            </Field>
          </div>
        </Card>

        {/* stats */}
        <Card title="Headline stats" wide>
          <StatsEditor stats={s.stats} onSave={(next) => wrap(() => publicSiteApi.updateStats(next), 'Stats saved.')} busy={busy} />
        </Card>

        {/* contact */}
        <Card title="Contact & links" wide>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <Field label="Phone"><input className="input" defaultValue={s.contactPhone ?? ''} onBlur={(e) => saveContact(s, { contactPhone: e.target.value }, wrap)} /></Field>
            <Field label="Email"><input className="input" defaultValue={s.contactEmail ?? ''} onBlur={(e) => saveContact(s, { contactEmail: e.target.value }, wrap)} /></Field>
            <Field label="Address"><input className="input" defaultValue={s.addressLine ?? ''} onBlur={(e) => saveContact(s, { addressLine: e.target.value }, wrap)} /></Field>
            <Field label="Google Maps link"><input className="input" defaultValue={s.mapUrl ?? ''} onBlur={(e) => saveContact(s, { mapUrl: e.target.value }, wrap)} /></Field>
            <Field label="Website"><input className="input" defaultValue={s.websiteUrl ?? ''} onBlur={(e) => saveContact(s, { websiteUrl: e.target.value }, wrap)} /></Field>
            <Field label="Facebook"><input className="input" defaultValue={s.facebookUrl ?? ''} onBlur={(e) => saveContact(s, { facebookUrl: e.target.value }, wrap)} /></Field>
            <Field label="Instagram"><input className="input" defaultValue={s.instagramUrl ?? ''} onBlur={(e) => saveContact(s, { instagramUrl: e.target.value }, wrap)} /></Field>
            <Field label="YouTube"><input className="input" defaultValue={s.youtubeUrl ?? ''} onBlur={(e) => saveContact(s, { youtubeUrl: e.target.value }, wrap)} /></Field>
          </div>
        </Card>

        {/* sections */}
        <Card title="Sections shown" wide>
          <div style={{ display: 'flex', gap: 18, flexWrap: 'wrap' }}>
            {([['showAchievements', 'Achievements'], ['showGallery', 'Gallery'], ['showAbout', 'About'], ['showStats', 'Stats'], ['showContact', 'Contact']] as const).map(([k, label]) => (
              <label key={k} style={{ display: 'flex', gap: 6, alignItems: 'center', fontSize: 14, cursor: 'pointer' }}>
                <input type="checkbox" checked={s[k]}
                  onChange={(e) => wrap(() => publicSiteApi.updateSections({
                    achievements: k === 'showAchievements' ? e.target.checked : s.showAchievements,
                    gallery: k === 'showGallery' ? e.target.checked : s.showGallery,
                    about: k === 'showAbout' ? e.target.checked : s.showAbout,
                    stats: k === 'showStats' ? e.target.checked : s.showStats,
                    contact: k === 'showContact' ? e.target.checked : s.showContact,
                  }), 'Saved.')} />
                {label}
              </label>
            ))}
          </div>
        </Card>
      </div>
    </div>
  );
}

function saveAbout(s: PublicSiteAdmin, patch: { title?: string; body?: string; year?: number | null; boards?: string }, wrap: (fn: () => Promise<PublicSiteAdmin>, ok: string) => void) {
  wrap(() => publicSiteApi.updateAbout({
    title: patch.title !== undefined ? (patch.title || null) : s.aboutTitle,
    body: patch.body !== undefined ? (patch.body || null) : s.aboutBody,
    establishedYear: patch.year !== undefined ? patch.year : s.establishedYear,
    boards: patch.boards !== undefined ? (patch.boards || null) : s.boardsOffered,
  }), 'Saved.');
}

function saveContact(s: PublicSiteAdmin, patch: Partial<PublicSiteAdmin>, wrap: (fn: () => Promise<PublicSiteAdmin>, ok: string) => void) {
  wrap(() => publicSiteApi.updateContact({ ...s, ...patch }), 'Saved.');
}

function StatsEditor({ stats, onSave, busy }: { stats: SiteStat[]; onSave: (s: SiteStat[]) => void; busy: boolean }) {
  const [rows, setRows] = useState<SiteStat[]>(stats.length ? stats : [{ label: '', value: '' }]);
  const set = (i: number, k: 'label' | 'value', v: string) => setRows(rows.map((r, j) => j === i ? { ...r, [k]: v } : r));
  return (
    <div>
      <div style={{ display: 'grid', gap: 8 }}>
        {rows.map((r, i) => (
          <div key={i} style={{ display: 'flex', gap: 8 }}>
            <input className="input" placeholder="Value (2,000+)" value={r.value} onChange={(e) => set(i, 'value', e.target.value)} style={{ width: 140 }} />
            <input className="input" placeholder="Label (Students)" value={r.label} onChange={(e) => set(i, 'label', e.target.value)} style={{ flex: 1 }} />
            <button className="btn" onClick={() => setRows(rows.filter((_, j) => j !== i))}>✕</button>
          </div>
        ))}
      </div>
      <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
        {rows.length < 8 && <button className="btn" onClick={() => setRows([...rows, { label: '', value: '' }])}>+ Add stat</button>}
        <button className="btn btn-accent" disabled={busy} onClick={() => onSave(rows.filter((r) => r.label && r.value))}>Save stats</button>
      </div>
    </div>
  );
}

function Card({ title, children, wide }: { title: string; children: React.ReactNode; wide?: boolean }) {
  return (
    <div className="card" style={{ padding: 18, ...(wide ? { gridColumn: '1 / -1' } : {}) }}>
      <div style={{ fontWeight: 600, marginBottom: 12 }}>{title}</div>
      {children}
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div style={{ marginBottom: 12, flex: 1 }}>
      <label className="label" style={{ fontSize: 12 }}>{label}</label>
      {children}
    </div>
  );
}
